import { AloftArea } from '@aloft/toolkit-shared';
import { Block, Flex, Input, Typography } from '@aloft/toolkit-sui';

import { Controller, useWatch } from 'react-hook-form';

import { useZodFormContext } from '../../../../hooks/useZodForm.ts';
import { RecommendCompsFormSchema } from '../../types.ts';
import { filterHasValue } from '../../utils/filterHasValue.ts';
import { EMPTY_RANGE_FILTER, FilterSection, WithinRangeMode } from './FilterSection.tsx';
import { SteppedPercentInput } from './SteppedPercentInput.tsx';

const MODE_TO_MATCH_CRITERIA: Record<WithinRangeMode, 'percentage_range' | 'min_max_range'> = {
  within: 'percentage_range',
  range: 'min_max_range',
};

const SubjectAreaField = ({ subjectArea }: { subjectArea?: AloftArea }) => (
  <Flex alignItems="center" gap="4px" height="40px" flex="1 1 0" minWidth={0}>
    <Typography variant="body2" color="textPrimary">
      {subjectArea ? subjectArea.toSqFtValueStringRounded() : ''}
    </Typography>
    <Typography variant="body2" color="textTertiary">
      sf
    </Typography>
  </Flex>
);

export const PercentageAreaFilter = ({
  label,
  fieldName,
  subjectArea,
}: {
  label: string;
  fieldName: 'gla' | 'lotSize';
  subjectArea?: AloftArea;
}) => {
  const { control, setValue } = useZodFormContext<RecommendCompsFormSchema>();
  const matchCriteria = useWatch({ control, name: `search.matchCriteria.${fieldName}` });
  const filterValue = useWatch({ control, name: `search.filters.${fieldName}` });
  const mode: WithinRangeMode = matchCriteria === 'min_max_range' ? 'range' : 'within';

  const handleModeChange = (nextMode: WithinRangeMode) => {
    setValue(`search.filters.${fieldName}`, { ...EMPTY_RANGE_FILTER });
    setValue(`search.matchCriteria.${fieldName}`, MODE_TO_MATCH_CRITERIA[nextMode]);
  };

  return (
    <FilterSection
      label={label}
      mode={mode}
      onModeChange={handleModeChange}
      showClear={filterHasValue(filterValue)}
      onClear={() => setValue(`search.filters.${fieldName}`, { ...EMPTY_RANGE_FILTER })}
    >
      {mode === 'within' ? (
        <Flex alignItems="center" gap="12px" minWidth={0} width="100%">
          <SubjectAreaField subjectArea={subjectArea} />
          <Block flex="1 1 0" minWidth={0}>
            <Controller
              name={`search.filters.${fieldName}.percentage`}
              control={control}
              render={({ field }) => (
                <SteppedPercentInput value={field.value ?? ''} onChange={field.onChange} />
              )}
            />
          </Block>
        </Flex>
      ) : (
        <Flex gap="8px" minWidth={0} width="100%">
          <Block flex="1 1 0" minWidth={0}>
            <Controller
              name={`search.filters.${fieldName}.min`}
              control={control}
              render={({ field }) => (
                <Input
                  id={`${fieldName}-min`}
                  size="sm"
                  type="aloftArea"
                  unit="sqft"
                  placeholderText="No min"
                  value={(field.value || undefined) as AloftArea | undefined}
                  onChange={field.onChange}
                  onClear={() => field.onChange('')}
                />
              )}
            />
          </Block>
          <Block flex="1 1 0" minWidth={0}>
            <Controller
              name={`search.filters.${fieldName}.max`}
              control={control}
              render={({ field }) => (
                <Input
                  id={`${fieldName}-max`}
                  size="sm"
                  type="aloftArea"
                  unit="sqft"
                  placeholderText="No max"
                  value={(field.value || undefined) as AloftArea | undefined}
                  onChange={field.onChange}
                  onClear={() => field.onChange('')}
                />
              )}
            />
          </Block>
        </Flex>
      )}
    </FilterSection>
  );
};
